import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans } from 'react-i18next'

import { AIEmojisAPI } from '@makyoapp/makyo-sdk'
import { AppBreakpoint } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  ActionFlag,
  BuilderDialog,
  IActionRepeaterValue,
  IActionType,
  VariableTextFeatureFlag,
} from '@makyoapp/makyo-common'
import { AlignCenter16, AlignLeft16, AlignRight16, Dialog16 } from '@makyoapp/makyo-icon/icon'
import { Button, Field, MaterialIconPicker, SingleIconSelect, Switch, Tabs } from '@makyoapp/makyo-ui'

import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useVariableTextSingleCell } from '@/hooks/__project__/useVariableTextSingleCell'
import { useActiveDevice } from '@/hooks/useActiveDevice'
import { useVariableTextEdit } from '@/hooks/useVariableTextEdit'
import { BUILDER_DEVICES } from '@/shared/builder-constants'
import { ComponentPanelTab } from '@/shared/builder-ui'
import { Accordion, AccordionItem } from '@/components/Accordion/Accordion'
import { BackButton } from '@/components/BackButton'
import { BrandKitColorsDropdown } from '@/components/BrandKitColorsDropdown'
import { DeviceTab } from '@/components/DeviceTab'
import { Help } from '@/components/Help'
import { TypographySelect } from '@/components/TypographySelect/TypographySelect'
import { VariableSimpleActionBody } from '@/components/VariableSelectV2/VariableSimpleAction'
import { VariableTextV2 } from '@/components/VariableTextV2/VariableTextV2'

import { SliderFieldSetting } from '../ComponentPanel/FieldSetting'

const iconAlignmentOptions = [
  {
    icon: <AlignLeft16 />,
    id: 'left',
    title: 'Start',
  },
  {
    icon: <AlignCenter16 />,
    id: 'center',
    title: 'Center',
  },
  {
    icon: <AlignRight16 />,
    id: 'right',
    title: 'End',
  },
]

interface DialogSettingDataProps {
  dialog: BuilderDialog
  search: string
}

export const DialogSettingData = ({ dialog: { id: dialogId }, search }: DialogSettingDataProps) => {
  const { dialogs, updateDialog, setDialogAction, navbarSettings } = useAppBuilderContext()
  const [activeDevice, setActiveDevice] = useActiveDevice()

  const activeNavbar = navbarSettings[activeDevice]
  const device = activeNavbar.viewport as AppBreakpoint
  const devices = Object.keys(BUILDER_DEVICES) as AppBreakpoint[]

  const dialogIdx = useMemo(() => {
    return dialogs.findIndex((d) => d.id === dialogId)
  }, [dialogs, dialogId])

  const dialog = dialogs[dialogIdx]

  const [showPicker, setShowPicker] = useState(false)

  // handle dialog contents with variable text support
  const [varDialogName, setVarDialogName] = useVariableTextEdit({
    value: dialog.name,
    onChange: (value) => {
      updateDialog(dialogIdx, (prev) => {
        prev.name = value
      })
    },
  })

  const [varDialogHeadline, setVarDialogHeadline] = useVariableTextEdit({
    value: dialog.headline,
    onChange: (value) => {
      updateDialog(dialogIdx, (prev) => {
        prev.headline = value
      })
    },
  })

  const [varDialogContent, setVarDialogContent] = useVariableTextEdit({
    value: dialog.content,
    onChange: (value) => {
      updateDialog(dialogIdx, (prev) => {
        prev.content = value
      })
    },
  })

  const [varLeadingLabel, setVarLeadingLabel] = useVariableTextEdit({
    value: dialog.leadingLabel,
    onChange: (value) => {
      updateDialog(dialogIdx, (prev) => {
        prev.leadingLabel = value
      })
    },
  })

  const [varTrailingLabel, setVarTrailingLabel] = useVariableTextEdit({
    value: dialog.trailingLabel,
    onChange: (value) => {
      updateDialog(dialogIdx, (prev) => {
        prev.trailingLabel = value
      })
    },
  })

  // debounce for width changes
  const widthTimeoutRef = useRef<NodeJS.Timeout>()

  // Get default width based on device
  const getDefaultWidth = (device: AppBreakpoint): string => {
    const defaults = {
      phone: '325px',
      'phone-landscape': '400px',
      tablet: '450px',
      'tablet-landscape': '500px',
      desktop: '530px',
      tv: '610px',
    }
    return defaults[device]
  }

  const [tempWidth, setTempWidth] = useState<string>(dialog.width?.[device] || getDefaultWidth(device))

  const debouncedUpdateWidth = useCallback(
    (newWidth: string) => {
      if (widthTimeoutRef.current) {
        clearTimeout(widthTimeoutRef.current)
      }

      widthTimeoutRef.current = setTimeout(() => {
        updateDialog(dialogIdx, (prev) => {
          // Preserve existing values and only update the current device
          const updatedWidth = {
            ...prev.width,
            [device]: newWidth,
          }

          // Ensure all breakpoints have values by using existing values or sensible defaults
          const defaultWidths = {
            phone: '325px',
            'phone-landscape': '400px',
            tablet: '450px',
            'tablet-landscape': '500px',
            desktop: '530px',
            tv: '610px',
          }

          prev.width = {
            ...defaultWidths,
            ...updatedWidth,
          }
        })
      }, 300) // 300ms delay
    },
    [updateDialog, dialogIdx, device]
  )

  // cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (widthTimeoutRef.current) {
        clearTimeout(widthTimeoutRef.current)
      }
    }
  }, [])

  // update tempWidth when device changes
  useEffect(() => {
    setTempWidth(dialog.width?.[device] || getDefaultWidth(device))
  }, [device, dialog.width])

  const getFilteredAccordionItems = useCallback(() => {
    if (!search.trim()) {
      return {
        showConfiguration: true,
        showContent: true,
        showLabels: true,
      }
    }

    const searchLower = search.toLowerCase()

    return {
      showConfiguration: 'data'.includes(searchLower) || 'name'.includes(searchLower),
      showContent:
        'content'.includes(searchLower) ||
        'dialog content'.includes(searchLower) ||
        'headline'.includes(searchLower) ||
        'content'.includes(searchLower) ||
        'icon'.includes(searchLower) ||
        'preview'.includes(searchLower),
      showLabels:
        'button labels'.includes(searchLower) ||
        'leading button'.includes(searchLower) ||
        'trailing button'.includes(searchLower),
    }
  }, [search])

  const filteredItems = getFilteredAccordionItems()

  return (
    <Accordion openMultiple={false} initialActiveItems={['dialog-configuration']}>
      {filteredItems.showConfiguration && (
        <AccordionItem itemId='dialog-configuration' title={<Trans>Configuration</Trans>}>
          <Field isVertical>
            <label>
              <Trans>Name</Trans>
            </label>
            <VariableTextV2
              value={varDialogName}
              onChange={setVarDialogName}
              featureFlags={VariableTextFeatureFlag.SingleCell}
              variableColumnOptions={{
                columnTypes: [ColumnTypes.Text],
              }}
            />
            {/* <SimpleInput
              required
              value={tempName}
              onChange={(e) => {
                const value = e.currentTarget.value
                setTempName(value)
              }}
              onBlur={async () => {
                if (tempName && tempName !== dialog.name) {
                  await updateDialog(dialogIdx, (prev) => {
                    prev.name = tempName
                  })
                }
              }}
            /> */}
          </Field>
        </AccordionItem>
      )}

      {filteredItems.showContent && (
        <AccordionItem itemId='dialog-content' title={<Trans>Dialog Content</Trans>}>
          <Field isVertical>
            <label className='flex-c'>
              <div>
                <Trans>Headline</Trans>{' '}
                <span className='text-light'>
                  <Trans>(optional)</Trans>
                </span>
              </div>
              <Help>Max characters = 60</Help>
            </label>

            <VariableTextV2
              value={varDialogHeadline}
              onChange={setVarDialogHeadline}
              featureFlags={VariableTextFeatureFlag.SingleCell}
              variableColumnOptions={{
                columnTypes: [ColumnTypes.Text],
              }}
            />
            {/* <SimpleInput
              value={tempHeadline}
              onChange={(e) => {
                const value = e.currentTarget.value
                if (value.length <= 60) {
                  setTempHeadline(value)
                }
              }}
              onBlur={async () => {
                if (tempHeadline !== dialog.headline) {
                  await updateDialog(dialogIdx, (prev) => {
                    prev.headline = tempHeadline
                  })
                }
              }}
            />
            <div
              className={clsx('text-m-sm text-light w-full text-end', (dialog.headline?.length ?? 0) === 60 && 'red')}
            >
              {60 - (dialog.headline?.length ?? 0)} <Trans>characters remaining</Trans>
            </div> */}
          </Field>
          <Field isVertical>
            <label className='flex-c'>
              <Trans>Content</Trans>
              <Help>Max characters = 250</Help>
            </label>

            <VariableTextV2
              value={varDialogContent}
              onChange={setVarDialogContent}
              featureFlags={VariableTextFeatureFlag.SingleCell}
              variableColumnOptions={{
                columnTypes: [ColumnTypes.Text],
              }}
              isTextArea
            />

            {/* <TextArea
              value={tempContent}
              onChange={(e) => {
                const value = e.currentTarget.value
                if (value.length <= 250) {
                  setTempContent(value)
                }
              }}
              onBlur={async () => {
                if (tempContent !== dialog.content) {
                  await updateDialog(dialogIdx, (prev) => {
                    prev.content = tempContent
                  })
                }
              }}
            />
            <div
              className={clsx('text-m-sm text-light w-full text-end', (dialog.content?.length ?? 0) === 250 && 'red')}
            >
              {250 - (dialog.content?.length ?? 0)} <Trans>characters remaining</Trans>
            </div> */}
          </Field>
          <Field>
            <label className='w-full'>
              <Trans>Icon</Trans>
            </label>
            <MaterialIconPicker
              onClose={() => setShowPicker(false)}
              show={showPicker}
              clickAsDone
              onToggle={() => setShowPicker(true)}
              onDone={async (icon) => {
                await updateDialog(dialogIdx, (prev) => {
                  prev.icon = icon
                })
                setShowPicker(false)
              }}
              searchMs={300}
              searchFilter={async (keyword) => {
                const aiSearch = await AIEmojisAPI.ReqFindEmoji(keyword)
                return aiSearch
              }}
              value={dialog.icon}
              className='rounded-md bg-zinc-200 dark:bg-slate-600'
              toggleClassName='p-2'
              portalMenu
              settings={{
                fill: 0,
                opsz: 24,
                wght: 300,
                grade: 0,
              }}
            />
          </Field>
          {/* <Field className='justify-end'>
            <Button
              className='w-full'
              icon={<Dialog16 />}
              onClick={() => {
                setDialogAction({
                  flag: ActionFlag.Preview | ActionFlag.Edit,
                  dialog,
                })
              }}
            >
              <Trans>Preview</Trans>
            </Button>
          </Field> */}
        </AccordionItem>
      )}

      {filteredItems.showLabels && (
        <AccordionItem itemId='dialog-button-labels' title={<Trans>Button Labels</Trans>}>
          {dialog.showLeading && (
            <Field isVertical>
              <label>
                <Trans>Leading Button</Trans>
              </label>

              <VariableTextV2
                value={varLeadingLabel}
                onChange={setVarLeadingLabel}
                featureFlags={VariableTextFeatureFlag.SingleCell}
                variableColumnOptions={{
                  columnTypes: [ColumnTypes.Text],
                }}
              />

              {/* <SimpleInput
                  value={tempLeadingLabel}
                  onChange={(e) => {
                    const value = e.currentTarget.value
                    setTempLeadingLabel(value)
                  }}
                  onBlur={async (e) => {
                    await updateDialog(dialogIdx, (prev) => {
                      prev.leadingLabel = tempLeadingLabel
                    })
                  }}
                /> */}
            </Field>
          )}
          {dialog.showTrailing && (
            <>
              <Field isVertical>
                <label>
                  <Trans>Trailing Button</Trans>
                </label>

                <VariableTextV2
                  value={varTrailingLabel}
                  onChange={setVarTrailingLabel}
                  featureFlags={VariableTextFeatureFlag.SingleCell}
                  variableColumnOptions={{
                    columnTypes: [ColumnTypes.Text],
                  }}
                />

                {/* <SimpleInput
                  value={tempTrailingLabel}
                  onChange={(e) => {
                    const value = e.currentTarget.value
                    setTempTrailingLabel(value)
                  }}
                  onBlur={async () => {
                    await updateDialog(dialogIdx, (prev) => {
                      prev.trailingLabel = tempTrailingLabel
                    })
                  }}
                /> */}
              </Field>
            </>
          )}
        </AccordionItem>
      )}

      {/* NOTE: hidden for now */}
      <div className='hidden'>
        <AccordionItem itemId='dialog-design' title={<Trans>Design</Trans>}>
          <Field isVertical>
            <label>Typography Headline</label>
            <TypographySelect
              value={{ typography: dialog.headlineTypography ?? 'Body Medium' } as any}
              // dialog.headlineTypography
              //   ? {  }
              //   : ({ typography: 'Body Medium' } as any)
              // }
              onChange={(value) => {
                updateDialog(dialogIdx, (prev) => {
                  prev.headlineTypography = value.typography
                })
              }}
            />
          </Field>
          <Field isVertical>
            <label>Typography Content</label>
            <TypographySelect
              value={{ typography: dialog.contentTypography ?? 'Body Medium' } as any}
              onChange={(value) => {
                updateDialog(dialogIdx, (prev) => {
                  prev.contentTypography = value.typography
                })
              }}
            />
          </Field>
        </AccordionItem>

        <AccordionItem itemId='dialog-icon' title={<Trans>Icon</Trans>}>
          <Field>
            <label htmlFor='dialog-show-icon-switch'>
              <Trans>Show Icon</Trans>
            </label>
            <Switch
              id='dialog-show-icon-switch'
              checked={!!dialog.showIcon}
              onClick={async (isChecked) => {
                await updateDialog(dialogIdx, (prev) => {
                  prev.showIcon = isChecked
                })
              }}
            />
          </Field>
        </AccordionItem>

        <AccordionItem itemId='dialog-alignment' title={<Trans>Alignment</Trans>}>
          <Field>
            <label>
              <Trans>Icon</Trans>
            </label>
            <SingleIconSelect
              id='dialog-icon-alignment'
              options={iconAlignmentOptions}
              onClick={(value) => {
                updateDialog(dialogIdx, (prev) => {
                  prev.alignment.iconAlignment = value.id
                })
              }}
              selected={dialog.alignment?.iconAlignment ?? ''}
            />
          </Field>
          <Field>
            <label>
              <Trans>Title</Trans>
            </label>
            <SingleIconSelect
              id='dialog-title-alignment'
              options={iconAlignmentOptions}
              onClick={(value) => {
                updateDialog(dialogIdx, (prev) => {
                  prev.alignment.titleAlignment = value.id
                })
              }}
              selected={dialog.alignment?.titleAlignment ?? ''}
            />
          </Field>
          <Field>
            <label>
              <Trans>Content</Trans>
            </label>
            <SingleIconSelect
              id='dialog-content-alignment'
              options={iconAlignmentOptions}
              onClick={(value) => {
                updateDialog(dialogIdx, (prev) => {
                  prev.alignment.contentAlignment = value.id
                })
              }}
              selected={dialog.alignment?.contentAlignment ?? ''}
            />
          </Field>
          <Field>
            <label>
              <Trans>Footer</Trans>
            </label>
            <SingleIconSelect
              id='dialog-footer-alignment'
              options={iconAlignmentOptions}
              onClick={(value) => {
                updateDialog(dialogIdx, (prev) => {
                  prev.alignment.footerAlignment = value.id
                })
              }}
              selected={dialog.alignment?.footerAlignment ?? ''}
            />
          </Field>
        </AccordionItem>

        <AccordionItem itemId='dialog-width' title={<Trans>Width</Trans>}>
          <div className='flex overflow-hidden rounded-sm mb-4'>
            {devices.map((d) => (
              <DeviceTab key={d} device={d} isActive={device === d} onClick={() => setActiveDevice(d)} />
            ))}
          </div>

          <Field isVertical>
            <div className='flex items-center justify-between w-full'>
              <label>
                <Trans>Width</Trans>
              </label>
              <p className='text-xs text-light flex justify-end'>{dialog.width?.[device]}</p>
            </div>
            <SliderFieldSetting
              id='dialog-width'
              settingId='dialog-width'
              value={tempWidth || dialog.width?.[device]}
              sliderSettings={{
                max: dialog.width && dialog.width?.[device]?.startsWith('%') ? 100 : 1000,
                min: 10,
                unit: '%',
                showUnit: true,
              }}
              onChange={(value) => {
                const newWidth = value[0] ?? '350px'
                setTempWidth(newWidth)
                debouncedUpdateWidth(newWidth)
              }}
            />
          </Field>
        </AccordionItem>

        <AccordionItem itemId='dialog-leading-actions' title={<Trans>Leading Button</Trans>}>
          <Field>
            <label htmlFor='dialog-footer-leading-switch'>
              <Trans>Enabled</Trans>
            </label>
            <Switch
              id='dialog-footer-leading-switch'
              checked={dialog.showLeading}
              onClick={async (isChecked) => {
                await updateDialog(dialogIdx, (prev) => {
                  prev.showLeading = isChecked
                })
              }}
            />
          </Field>

          {dialog.showLeading && (
            <>
              <Field isVertical>
                <label>
                  <Trans>Label</Trans>
                </label>

                <VariableTextV2
                  value={varLeadingLabel}
                  onChange={setVarLeadingLabel}
                  featureFlags={VariableTextFeatureFlag.SingleCell}
                  variableColumnOptions={{
                    columnTypes: [ColumnTypes.Text],
                  }}
                />

                {/* <SimpleInput
                  value={tempLeadingLabel}
                  onChange={(e) => {
                    const value = e.currentTarget.value
                    setTempLeadingLabel(value)
                  }}
                  onBlur={async (e) => {
                    await updateDialog(dialogIdx, (prev) => {
                      prev.leadingLabel = tempLeadingLabel
                    })
                  }}
                /> */}
              </Field>

              <Field isVertical>
                <label>
                  <Trans>Action</Trans>
                </label>

                {/* <ActionRepeater
                  label=''
                  id={`dialog-${dialogId}-leading-button-actions`}
                  values={dialog.leadingActions ?? []}
                  onChange={async (opt) => {
                    await updateDialog(dialogIdx, (prev) => {
                      prev.leadingActions = opt
                    })
                  }}
                  excludeActions={excludedActionsForDialog}
                /> */}

                <div className='flex flex-col gap-2'>
                  <VariableSimpleActionBody
                    id='dialog-leading-action'
                    value={
                      dialog.leadingActions?.[0] ?? {
                        type: IActionType.CloseAlert,
                        settings: {},
                      }
                    }
                    onChange={async (opt) => {
                      await updateDialog(dialogIdx, (prev) => {
                        prev.leadingActions = [opt as IActionRepeaterValue]
                      })
                    }}
                    actionTypes={[IActionType.CloseAlert, IActionType.PerformNextAction]}
                  />
                </div>
              </Field>

              <Field isVertical>
                <label>Color</label>
                <BrandKitColorsDropdown
                  value={dialog.leadingButtonColor || 'primary-50'}
                  onChange={(colorVariant) => {
                    updateDialog(dialogIdx, (prev) => {
                      prev.leadingButtonColor = colorVariant
                    })
                  }}
                  showExtendedColors={true}
                />
              </Field>
            </>
          )}
        </AccordionItem>

        <AccordionItem itemId='dialog-trailing' title={<Trans>Trailing Button</Trans>}>
          <Field>
            <label htmlFor='dialog-footer-trailing-switch'>
              <Trans>Enabled</Trans>
            </label>
            <Switch
              id='dialog-footer-trailing-switch'
              checked={dialog.showTrailing}
              onClick={async (isChecked) => {
                await updateDialog(dialogIdx, (prev) => {
                  prev.showTrailing = isChecked
                })
              }}
            />
          </Field>

          {dialog.showTrailing && (
            <>
              <Field isVertical>
                <label>
                  <Trans>Label</Trans>
                </label>

                <VariableTextV2
                  value={varTrailingLabel}
                  onChange={setVarTrailingLabel}
                  featureFlags={VariableTextFeatureFlag.SingleCell}
                  variableColumnOptions={{
                    columnTypes: [ColumnTypes.Text],
                  }}
                />

                {/* <SimpleInput
                  value={tempTrailingLabel}
                  onChange={(e) => {
                    const value = e.currentTarget.value
                    setTempTrailingLabel(value)
                  }}
                  onBlur={async () => {
                    await updateDialog(dialogIdx, (prev) => {
                      prev.trailingLabel = tempTrailingLabel
                    })
                  }}
                /> */}
              </Field>

              <Field isVertical>
                <label>
                  <Trans>Action</Trans>
                </label>

                <VariableSimpleActionBody
                  id='dialog-trailing-action'
                  value={
                    dialog.trailingActions?.[0] ?? {
                      type: IActionType.CloseAlert,
                      settings: {},
                    }
                  }
                  onChange={async (opt) => {
                    await updateDialog(dialogIdx, (prev) => {
                      prev.trailingActions = [opt as IActionRepeaterValue]
                    })
                  }}
                  actionTypes={[IActionType.CloseAlert, IActionType.PerformNextAction]}
                />
              </Field>

              <Field isVertical>
                <label>Color</label>
                <BrandKitColorsDropdown
                  value={dialog.trailingButtonColor || 'neutral-100'}
                  onChange={(colorVariant) => {
                    updateDialog(dialogIdx, (prev) => {
                      prev.trailingButtonColor = colorVariant
                    })
                  }}
                  showExtendedColors={true}
                />
              </Field>
            </>
          )}
        </AccordionItem>

        <AccordionItem itemId='dialog-divider' title={<Trans>Footer Divider</Trans>}>
          <Field>
            <label htmlFor='dialog-divider-switch'>
              <Trans>Enabled</Trans>
            </label>
            <Switch
              id='dialog-divider-switch'
              checked={dialog.showDivider}
              onClick={async (isChecked) => {
                await updateDialog(dialogIdx, (prev) => {
                  prev.showDivider = isChecked
                })
              }}
            />
          </Field>
        </AccordionItem>
      </div>
    </Accordion>
  )
}
